import { useCallback, useState } from 'react';
import { presetFor } from '@app/shared';
import type { StaffDto } from '../../api/cabinetTypes';
import { ApiFailure } from '../../api/http';
import { useAppApi } from '../../app/ApiProvider';
import { useAsync } from '../../hooks/useAsync';
import { capitalize } from '../../i18n/dict';
import { useI18n } from '../../i18n/I18nProvider';
import { BottomSheet } from '../../ui/BottomSheet';
import { CtaBar, CtaButton, SmallButton } from '../../ui/Cta';
import { Avatar, Tag } from '../../ui/controls';
import { Alert, EmptyState, ErrorState, Loader } from '../../ui/feedback';
import { Screen, ScrollArea, Section, SectionTitle, TopBar } from '../../ui/Screen';
import { Field, FormError, Note, Row, TextInput } from './settings/parts';
import { staffOffWarnings, type StaffOffWarnings } from './settings/staffWarning';
import { useBusiness } from './useBusiness';

/**
 * «Специалисты» кабинета (ТЗ §14.1). Механика та же, что у услуг: удаления нет, есть
 * тумблер активности, выключенные остаются в списке.
 *
 * ДВА ОТЛИЧИЯ ОТ УСЛУГ, И ОБА СУЩЕСТВЕННЫЕ.
 *
 * Первое: специалист называется по-разному в разных отраслях — мастер, врач,
 * преподаватель (ТЗ §3). Термин берётся из пресета отрасли. Это единственное, для чего
 * пресет читать МОЖНО: правила организации из пресета читать нельзя (AGENTS.md, правило 5),
 * они скопированы в её строку, а термин и иконка в строке не хранятся вовсе.
 *
 * Второе: выключение мастера — не косметика. `setStaffActive` возвращает, сколько будущих
 * записей у него осталось, и сервис НАМЕРЕННО не отменяет их сам: отмена шлёт клиенту
 * уведомление и делается по каждой осознанно (ТЗ §11.4). Это число показывается всегда,
 * когда оно есть, — иначе клиенты придут к пустому креслу.
 */
export function StaffScreen(props: { businessId: string; onBack: () => void }): JSX.Element {
  const { t } = useI18n();
  const api = useAppApi();
  const { businessId } = props;

  const businessLoad = useBusiness(businessId);
  const list = useAsync(
    useCallback(() => api.cabinet.listStaff(businessId), [api, businessId]),
    [businessId],
  );

  const [form, setForm] = useState<StaffForm | null>(null);
  const [nameProblem, setNameProblem] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [toggleError, setToggleError] = useState<unknown>(null);
  /**
   * Что показать после переключения мастера: сколько будущих записей у него осталось
   * и не опустела ли витрина организации. `null` — предупреждать не о чем.
   *
   * Одно состояние на оба повода, а не два: поводы независимы и выпадают вместе,
   * а две шторки наложились бы друг на друга.
   */
  const [warnings, setWarnings] = useState<StaffOffWarnings | null>(null);

  const business = businessLoad.business;
  // Термин отрасли: «мастер», «врач», «преподаватель». Строчный — заглавную ставит capitalize.
  const term = business ? t(presetFor(business.type).specialistTerm) : '';

  const closeForm = (): void => {
    setForm(null);
    setNameProblem(false);
    setSaveError(null);
  };

  const save = async (): Promise<void> => {
    if (!form || busy) return;
    const name = form.name.trim();
    if (!name) {
      setNameProblem(true);
      return;
    }
    // Пустое поле — это снятие специализации, а не «не трогать»: иначе стереть её
    // было бы нечем.
    const title = form.title.trim() === '' ? null : form.title.trim();

    setBusy(true);
    setNameProblem(false);
    setSaveError(null);
    try {
      if (form.id) await api.cabinet.updateStaff(businessId, form.id, name, title);
      else await api.cabinet.createStaff(businessId, name, title);
      closeForm();
      list.reload();
    } catch (error) {
      setSaveError(error);
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (member: StaffDto): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setToggleError(null);
    try {
      const result = await api.cabinet.setStaffActive(businessId, member.id, !member.active);
      setWarnings(staffOffWarnings(result));
      list.reload();
    } catch (error) {
      setToggleError(error);
    } finally {
      setBusy(false);
    }
  };

  if (businessLoad.state.status === 'loading' || list.state.status === 'loading') {
    return (
      <Screen>
        <TopBar title={t('stfTitle')} onBack={props.onBack} />
        <Loader />
      </Screen>
    );
  }

  if (businessLoad.state.status === 'failed' || !business) {
    // Без организации неизвестна отрасль, а значит и как называть специалиста. Подставить
    // «мастера» нельзя: у стоматологии это прямая ложь на каждой кнопке экрана.
    return (
      <Screen>
        <TopBar title={t('stfTitle')} onBack={props.onBack} />
        <ErrorState
          // Ветка `!business` при успешной загрузке — рассогласование внутри приложения,
          // а не отказ сервера. Сырой `new Error('нет организации')` показал бы русскую
          // строку мимо словаря: `ErrorState` переводит ключи, а обычной ошибке печатает
          // `message` как есть.
          error={
            businessLoad.state.status === 'failed'
              ? businessLoad.state.error
              : new ApiFailure('err.internal', 500)
          }
          onRetry={businessLoad.reload}
        />
      </Screen>
    );
  }

  return (
    <Screen>
      <TopBar title={t('stfTitle')} subtitle={business.name} onBack={props.onBack} />

      <ScrollArea bottomGap="cta">
        {list.state.status === 'failed' ? (
          <ErrorState error={list.state.error} onRetry={list.reload} />
        ) : (
          <Section>
            <SectionTitle>{capitalize(term)}</SectionTitle>
            {list.state.data.length === 0 ? (
              <EmptyState>{t('stfEmpty')}</EmptyState>
            ) : (
              list.state.data.map((member) => (
                <Row key={member.id} dimmed={!member.active}>
                  <div className="flex items-center justify-between gap-3">
                    <Avatar name={member.name} size="sm" />
                    <button
                      type="button"
                      onClick={() => {
                        setNameProblem(false);
                        setSaveError(null);
                        setForm({ id: member.id, name: member.name, title: member.title ?? '' });
                      }}
                      className="min-w-0 flex-1 text-left"
                    >
                      <div className="truncate text-[14.5px] font-semibold">{member.name}</div>
                      {member.title ? (
                        <div className="truncate text-[12px] text-ink-3">{member.title}</div>
                      ) : null}
                    </button>
                    <Tag tone={member.active ? 'ok' : 'cancelled'}>
                      {t(member.active ? 'itemActive' : 'itemInactive')}
                    </Tag>
                  </div>
                  <div className="mt-2.5 flex">
                    <SmallButton
                      variant={member.active ? 'ghost' : 'accent'}
                      disabled={busy}
                      onClick={() => void toggleActive(member)}
                    >
                      {t(member.active ? 'itemDisable' : 'itemEnable')}
                    </SmallButton>
                  </div>
                </Row>
              ))
            )}
            <Note>{t('itemNoDelete')}</Note>
          </Section>
        )}
      </ScrollArea>

      <CtaBar>
        <CtaButton
          onClick={() => {
            setNameProblem(false);
            setSaveError(null);
            setForm({ id: null, name: '', title: '' });
          }}
          disabled={list.state.status !== 'ready'}
        >
          {t('stfAddTerm', { term })}
        </CtaButton>
      </CtaBar>

      <BottomSheet
        open={form !== null}
        title={t(form?.id ? 'stfEditTerm' : 'stfAddTerm', { term })}
        onClose={closeForm}
      >
        {form ? (
          <>
            <Field label={t('stfName')}>
              <TextInput
                value={form.name}
                onChange={(value) => setForm({ ...form, name: value })}
                placeholder={t('stfPh')}
              />
            </Field>

            <Field label={t('stfTitle2')}>
              <TextInput
                value={form.title}
                onChange={(value) => setForm({ ...form, title: value })}
                placeholder={t('titlePh')}
              />
            </Field>
            <Note>{t('stfTitleNote')}</Note>

            {nameProblem ? <FormError text={t('stfErrName')} /> : null}
            {saveError ? <ErrorState error={saveError} withTitle={false} /> : null}
            {form.id ? <Note>{t('itemNoDelete')}</Note> : null}

            <CtaButton onClick={() => void save()} disabled={busy}>
              {t('itemSave')}
            </CtaButton>
            <CtaButton variant="ghost" onClick={closeForm} className="mt-2">
              {t('itemCancel')}
            </CtaButton>
          </>
        ) : null}
      </BottomSheet>

      {/* Предупреждения после переключения мастера. Отдельная шторка, а не строка
          в списке: её нельзя пролистать мимо, а разбирать последствия придётся руками —
          записи отменять по каждой осознанно (ТЗ §11.4), организацию скрывать в настройках.

          Заголовок — по первому поводу: записи важнее, их ждут живые люди. */}
      <BottomSheet
        open={warnings !== null}
        title={t(warnings !== null && warnings.appointments === null ? 'bizEmptyShelfT' : 'staffOffWarnT')}
        onClose={() => setWarnings(null)}
      >
        {warnings !== null && warnings.appointments !== null ? (
          <Alert tone="warn">{t('staffOffWarn', { count: warnings.appointments })}</Alert>
        ) : null}
        {warnings?.unbookableInCatalog ? <Alert tone="danger">{t('bizEmptyShelf')}</Alert> : null}
        <CtaButton variant="ghost" onClick={() => setWarnings(null)}>
          {t('close')}
        </CtaButton>
      </BottomSheet>

      <BottomSheet
        open={toggleError !== null}
        title={t('errTitle')}
        onClose={() => setToggleError(null)}
      >
        <ErrorState error={toggleError} withTitle={false} />
        <CtaButton variant="ghost" onClick={() => setToggleError(null)}>
          {t('close')}
        </CtaButton>
      </BottomSheet>
    </Screen>
  );
}

interface StaffForm {
  readonly id: string | null;
  readonly name: string;
  /** Специализация. Пустая строка = «не указана»: сервис приводит её к `null`. */
  readonly title: string;
}
